import { cn } from '@/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import type React from 'react';
import { useEffect, useId, useState } from 'react';
import { CustomArrow } from './custom-arrow';
import { type tooltipArrowSizeVariants, tooltipContentVariants, tooltipCustomArrowSizeVariants, tooltipCustomArrowVariants } from './styles';
const CLICK_TOOLTIP_OPEN_EVENT = 'uppbeat:click-tooltip-open';
export interface TooltipProps extends VariantProps<typeof tooltipContentVariants> {
  align?: TooltipPrimitive.TooltipContentProps['align'];
  alignOffset?: number;
  arrowSize?: VariantProps<typeof tooltipArrowSizeVariants>['size'];
  children: React.ReactNode;
  className?: string;
  delayDuration?: number;
  forceShow?: boolean;
  hideOnFocus?: boolean;
  label?: React.ReactNode;
  onCancelForceShow?: () => void;
  showOnClick?: boolean;
  side?: TooltipPrimitive.TooltipContentProps['side'];
  sideOffset?: number;
}
const Tooltip: React.FC<TooltipProps> = ({
  children,
  label,
  className,
  delayDuration = 500,
  side = 'top',
  align = 'center',
  hideOnFocus = true,
  variant,
  arrowSize,
  showOnClick = false,
  forceShow,
  onCancelForceShow,
  alignOffset,
  sideOffset = 5
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocusVisible, setIsFocusVisible] = useState(false);
  const tooltipId = useId();
  useEffect(() => {
    setIsOpen(forceShow ?? false);
  }, [forceShow]);
  useEffect(() => {
    if (!showOnClick) {
      return;
    }
    const handleOtherTooltipOpen = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== tooltipId) {
        setIsOpen(false);
      }
    };
    window.addEventListener(CLICK_TOOLTIP_OPEN_EVENT, handleOtherTooltipOpen);
    return () => {
      window.removeEventListener(CLICK_TOOLTIP_OPEN_EVENT, handleOtherTooltipOpen);
    };
  }, [showOnClick, tooltipId]);
  if (!label) {
    return children;
  }
  const handleClick = (e: React.MouseEvent) => {
    if (showOnClick) {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(prev => {
        const next = !prev;
        if (next) {
          window.dispatchEvent(new CustomEvent(CLICK_TOOLTIP_OPEN_EVENT, {
            detail: tooltipId
          }));
        }
        return next;
      });
    }
  };
  const handleCancelForceShow = () => {
    if (typeof onCancelForceShow === 'function' && forceShow) {
      onCancelForceShow();
    }
  };
  return <TooltipPrimitive.Provider delayDuration={delayDuration} data-sentry-element="TooltipPrimitive.Provider" data-sentry-component="Tooltip" data-sentry-source-file="index.tsx">
      <TooltipPrimitive.Root defaultOpen={false} disableHoverableContent onOpenChange={open => {
      setIsOpen(open);
      handleCancelForceShow();
    }} open={isOpen} data-sentry-element="TooltipPrimitive.Root" data-sentry-source-file="index.tsx">
        <TooltipPrimitive.Trigger asChild onBlur={() => {
        setIsFocusVisible(false);
      }} onClick={handleClick} onFocus={e => {
        try {
          if (e.target.matches(':focus-visible')) {
            setIsFocusVisible(true);
          }
        } catch {
          // Gracefully fail for browsers that don't support :focus-visible
        }
      }} data-sentry-element="TooltipPrimitive.Trigger" data-sentry-source-file="index.tsx">
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal data-sentry-element="TooltipPrimitive.Portal" data-sentry-source-file="index.tsx">
          <TooltipPrimitive.Content align={align} alignOffset={alignOffset} className={cn(tooltipContentVariants({
          variant
        }), className, !!isFocusVisible && hideOnFocus && 'sr-only')} collisionPadding={{
          top: 16,
          right: 16,
          bottom: 16,
          left: 16
        }} onClick={e => {
          e.stopPropagation();
        }} side={side} sideOffset={sideOffset} data-sentry-element="TooltipPrimitive.Content" data-sentry-source-file="index.tsx">
            {label}
            <TooltipPrimitive.Arrow asChild className={cn(tooltipCustomArrowVariants({
            variant
          }), tooltipCustomArrowSizeVariants({
            size: arrowSize
          }))} data-sentry-element="TooltipPrimitive.Arrow" data-sentry-source-file="index.tsx">
              <CustomArrow data-sentry-element="CustomArrow" data-sentry-source-file="index.tsx" />
            </TooltipPrimitive.Arrow>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>;
};
export default Tooltip;